import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { Timestamp } from "firebase/firestore";
import { errorHandler, successHandler } from "../../../helpers/handlers";
import { uploadDocument, deleteDocument } from "../../../config/firebase";
import api from "../../../config/axios";
import { logout } from "../auth";

const initialState = {
  active: {
    data: [],
  },
  inactive: {
    data: [],
    hasMoreContent: false,
    lastDocId: null,
  },
  selectedApplication: null,
  pageLoading: false,
  moreLoading: false,
  actionLoading: false,
  error: null,
};

const slice = createSlice({
  name: "applications",
  initialState,
  reducers: {
    // Reset data for applications if needed
    resetApplications: (state) => {
      state = initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout.fulfilled, () => initialState);
    builder
      .addCase(fetchActiveApplications.pending, (state) => {
        state.pageLoading = true;
      })
      .addCase(fetchActiveApplications.fulfilled, (state, action) => {
        const { data } = action.payload;
        state.active.data = data;
        state.pageLoading = false;
        state.error = null;
      })
      .addCase(fetchActiveApplications.rejected, (state, action) => {
        state.error = action.payload;
        state.pageLoading = false;
      })

      .addCase(fetchInactiveApplications.pending, (state) => {
        state.pageLoading = true;
        state.moreLoading = true;
      })
      .addCase(fetchInactiveApplications.fulfilled, (state, action) => {
        const { data, lastDocId } = action.payload;
        if (action.reset === true) {
          state.inactive.data = data;
        } else {
          state.inactive.data = [...state.inactive.data, ...data];
        }
        state.inactive.lastDocId = lastDocId;
        state.inactive.hasMoreContent = action.payload.hasMoreContent;
        state.pageLoading = false;
        state.moreLoading = false;
        state.error = null;
      })
      .addCase(fetchInactiveApplications.rejected, (state, action) => {
        state.error = action.payload;
        state.pageLoading = false;
        state.moreLoading = false;
      })
      .addCase(updateApplicationStatus.pending, () => {
        // currently we do nothing while updating status
      })
      .addCase(updateApplicationStatus.fulfilled, (state, action) => {
        // no need to do anything since state is managed locally in component
        if (action.payload.id === state.selectedApplication?.id) {
          state.selectedApplication.status = action.payload.status;
        }
      })
      .addCase(updateApplicationStatus.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchApplicationDetails.pending, (state) => {
        state.pageLoading = true;
      })
      .addCase(fetchApplicationDetails.fulfilled, (state, action) => {
        state.pageLoading = false;
        state.selectedApplication = action.payload;
      })
      .addCase(fetchApplicationDetails.rejected, (state, action) => {
        state.pageLoading = false;
        state.error = action.payload;
      })
      .addCase(updateApplicationAssignee.pending, () => {
        // currently we do nothing while updating assignee
      })
      .addCase(updateApplicationAssignee.fulfilled, (state, action) => {
        if (state.selectedApplication?.id === action.payload.id) {
          state.selectedApplication.assignee = action.payload.assignee;
        }
      })
      .addCase(updateApplicationAssignee.rejected, (state, action) => {
        state.error = action.payload;
        state.actionLoading = false;
      })
      .addCase(createApplicationTask.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createApplicationTask.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.selectedApplication.tasks = [
          ...action.payload,
          ...state.selectedApplication.tasks,
        ];
        state.error = null;
      })
      .addCase(createApplicationTask.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })
      .addCase(updateApplicationTask.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })

      .addCase(updateApplicationTask.fulfilled, (state, action) => {
        state.actionLoading = false;

        // Update the task in the selected application
        const updatedTask = action.payload;
        const tasks = state.selectedApplication?.tasks || [];
        const taskIndex = tasks.findIndex((task) => task.id === updatedTask.id);

        if (taskIndex !== -1) {
          tasks[taskIndex] = updatedTask;
        }

        // Sort the tasks after updating
        state.selectedApplication.tasks = tasks.sort((a, b) => {
          const statusOrder = ["accepted", "closed"];
          if (
            statusOrder.includes(a.status) &&
            !statusOrder.includes(b.status)
          ) {
            return 1;
          }
          if (
            !statusOrder.includes(a.status) &&
            statusOrder.includes(b.status)
          ) {
            return -1;
          }
          return 0;
        });

        state.error = null;
      })
      .addCase(updateApplicationTask.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      });
  },
});

// Fetch active applications
export const fetchActiveApplications = createAsyncThunk(
  "applications/fetchActive",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/applications?filter=active`);
      return {
        data: response.data.payload.data,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch inactive applications
export const fetchInactiveApplications = createAsyncThunk(
  "applications/fetchInactive",
  async ({ lastDocId = "", limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `/admin/applications?filter=inactive&limit=${limit}&lastDocId=${lastDocId}`
      );
      return {
        data: response.data.payload.data,
        lastDocId: response.data.payload.lastDocId,
        reset: lastDocId === "",
        hasMoreContent: response.data.payload.data.length === limit,
      };
    } catch (error) {
      console.error(error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const updateApplicationStatus = createAsyncThunk(
  "applications/status",
  async ({ id, status, notify = false }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/applications/${id}`, { status });
      if (notify) {
        successHandler(response.data.message);
      }
      const data = response.data.payload;
      return { id: data.id, status: data.status };
    } catch (error) {
      console.error(error.message);
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchApplicationDetails = createAsyncThunk(
  "applications/fetchDetails",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/applications/${id}`);
      return response.data.payload;
    } catch (error) {
      console.error(error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const updateApplicationAssignee = createAsyncThunk(
  "applications/updateAssignee",
  async ({ id, assignee }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/admin/applications/${id}/assignee`, {
        assignee,
      });
      const data = response.data.payload;
      successHandler(response.data.message);
      return { id: data.id, assignee: data.assignee };
    } catch (error) {
      console.error(error.message);
      return rejectWithValue(error.message);
    }
  }
);

export const createApplicationTask = createAsyncThunk(
  "applications/create",
  async ({ applicationId, taskData }, { rejectWithValue }) => {
    try {
      let attachmentUrl = null;
      if (taskData.attachment instanceof File) {
        attachmentUrl = await uploadDocument(
          taskData.attachment,
          "documents",
          "application"
        );
      }
      const payload = {
        ...taskData,
        attachment: attachmentUrl,
      };
      delete payload.status;
      if (!taskData.comment) {
        delete payload.comment;
      }
      if (!taskData.attachment) {
        delete payload.attachment;
      }
      if (!taskData.deadline) {
        delete payload.deadline;
      }

      const response = await api.post(
        `/admin/applications/${applicationId}/tasks`,
        payload
      );

      successHandler(response.data.message);
      return response.data.payload.map((task) => ({
        ...task,
        createdAt: Timestamp.now(),
      }));
    } catch (error) {
      console.error(error.message);
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateApplicationTask = createAsyncThunk(
  "applications/updateTask",
  async ({ applicationId, taskData }, { rejectWithValue }) => {
    try {
      let attachmentUrl = null;
      let oldAttachment = taskData.attachment;

      // Upload attachment if it's a file
      if (taskData.attachment && typeof taskData.attachment !== "string") {
        attachmentUrl = await uploadDocument(
          taskData.attachment,
          "documents",
          "application"
        );
      } else {
        oldAttachment = null;
      }

      // Prepare the payload
      const payload = {
        title: taskData.title,
        description: taskData.description,
        type: taskData.type,
        status: taskData.status,
      };

      if (taskData.comment) {
        payload.comment = taskData.comment;
      }
      if (taskData.attachment) {
        payload.attachment = attachmentUrl || taskData.attachment;
      }
      if (taskData.deadline) {
        payload.deadline = taskData.deadline;
      }

      const response = await api.put(
        `/admin/applications/${applicationId}/tasks/${taskData.id}`,
        payload
      );

      successHandler(response.data.message);
      deleteDocument(oldAttachment);
      return response.data.payload;
    } catch (error) {
      console.error(error.message);
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

// Export actions for use in components
export const { resetApplications } = slice.actions;

export default slice.reducer;
